import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import api from '../../api/axios';
import useAuthStore from '../../store/authStore';
import { getPlanDefinition, isFreePlan } from '../../utils/planAccess';
import UpgradeModal from '../../components/UpgradeModal';
import { fmtTime } from '../../utils/dateFormat';

const days = ['Bazar', 'Bazar ertəsi', 'Çərşənbə axşamı', 'Çərşənbə', 'Cümə axşamı', 'Cümə', 'Şənbə'];
const months = ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'İyun', 'İyul', 'Avqust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'];

const LockedPanel = ({ children, onUnlock }) => (
    <div className="relative">
        <div className="pointer-events-none blur-[2px]">{children}</div>
        <div className="absolute inset-0 flex items-center justify-center bg-white/55">
            <button
                type="button"
                onClick={onUnlock}
                className="rounded-2xl border border-amber-200 bg-white px-5 py-3 text-sm font-bold text-slate-900 shadow-lg"
            >
                Sadə və ya Pro plana keçin
            </button>
        </div>
    </div>
);

const formatLicenseDate = (value) => {
    const raw = String(value || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        return raw;
    }

    const [year, month, day] = raw.split('-');
    return `${day}-${month}-${year}`;
};

const Dashboard = () => {
    const { user } = useAuthStore();
    const [data, setData] = useState(null);
    const [licenseStatus, setLicenseStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const plan = getPlanDefinition(user?.tenant_plan);
    const freePlan = isFreePlan(user?.tenant_plan);

    useEffect(() => {
        loadDashboard();
    }, []);

    useEffect(() => {
        if (!freePlan || !user?.tenant_id) return;
        const key = `plan_modal_seen_${user.tenant_id}_${plan?.code || 'free'}`;
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, '1');
        setShowUpgradeModal(true);
    }, [freePlan, plan?.code, user?.tenant_id]);

    useEffect(() => {
        const openForcedModal = () => setShowUpgradeModal(true);
        window.addEventListener('fiscal-open-upgrade-modal', openForcedModal);
        if (sessionStorage.getItem('force_upgrade_modal') === '1') {
            sessionStorage.removeItem('force_upgrade_modal');
            setShowUpgradeModal(true);
        }
        return () => window.removeEventListener('fiscal-open-upgrade-modal', openForcedModal);
    }, []);

    const loadDashboard = async () => {
        try {
            const [dashboardRes, licenseRes] = await Promise.all([
                api.get('/reports/dashboard'),
                api.get('/auth/license-status')
            ]);

            setData(dashboardRes.data.data);
            setLicenseStatus(licenseRes.data?.data || null);
        } catch {
            toast.error('Məlumat yüklənmədi.');
        } finally {
            setLoading(false);
        }
    };

    const statusLabel = (status) => ({
        aciq: 'Açıq',
        baglanmis: 'Bağlı',
        legv: 'Ləğv'
    }[status] || status);

    const statusColor = (status) => ({
        aciq: 'bg-green-100 text-green-700',
        baglanmis: 'bg-blue-100 text-blue-700',
        legv: 'bg-red-100 text-red-700'
    }[status] || 'bg-gray-100 text-gray-600');

    if (loading) {
        return (
            <Layout>
                <div className="flex h-64 items-center justify-center">
                    <div className="text-lg text-gray-400">Yüklənir...</div>
                </div>
            </Layout>
        );
    }

    const now = new Date();
    const dateStr = `${days[now.getDay()]}, ${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}`;
    const licenseDaysLeft = Number.isFinite(Number(licenseStatus?.days_left))
        ? Number(licenseStatus.days_left)
        : null;
    const showLicenseBanner = Boolean(licenseStatus?.end_date);
    const licenseBannerClass = licenseDaysLeft !== null && licenseDaysLeft <= 3
        ? 'bg-red-50 text-red-700 border-red-200'
        : 'bg-emerald-50 text-emerald-700 border-emerald-200';

    const recentOrdersTable = (
        <div className="overflow-x-auto"><table className="w-full min-w-[560px]">
            <thead>
                <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-400">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-400">Masa</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-400">Ofisiant</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-400">Məbləğ</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-400">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-400">Vaxt</th>
                </tr>
            </thead>
            <tbody>
                {data?.recent_orders?.map((order) => (
                    <tr key={order.id} className="border-t border-gray-50 transition-colors hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-xs text-gray-400">#{order.id}</td>
                        <td className="px-4 py-3 text-sm font-semibold">{order.table_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{order.waiter_name}</td>
                        <td className="px-4 py-3 font-mono text-sm font-bold text-primary-600">
                            {parseFloat(order.total || 0).toFixed(2)} ₼
                        </td>
                        <td className="px-4 py-3">
                            <span className={`rounded-full px-2 py-1 text-xs font-bold ${statusColor(order.status)}`}>
                                {statusLabel(order.status)}
                            </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400">
                            {fmtTime(order.opened_at)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table></div>
    );

    const topItemsList = (
        <div>
            {data?.top_items?.map((item, index) => (
                <div key={`${item.name}-${index}`} className="flex items-center gap-3 border-b border-gray-50 px-5 py-4 last:border-0">
                    <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        index === 0
                            ? 'bg-amber-100 text-amber-600'
                            : index === 1
                                ? 'bg-gray-100 text-gray-500'
                                : index === 2
                                    ? 'bg-orange-100 text-orange-500'
                                    : 'bg-primary-50 text-primary-500'
                    }`}>
                        {index + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-gray-700">{item.name}</div>
                        <div className="text-xs text-gray-400">{(() => {
                            const n = Number(item.sold || 0);
                            return Number.isInteger(n) ? n : n.toFixed(2).replace(/\.?0+$/, '');
                        })()} ədəd</div>
                    </div>
                    <div className="font-mono text-sm font-bold text-primary-600">
                        {parseFloat(item.revenue || 0).toFixed(2)} ₼
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <Layout>
            <UpgradeModal
                open={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                title={`${plan?.label || 'Pulsuz'} paket istifadə edirsiniz`}
                message="Plan fərqlərini aşağıda görə bilərsiniz. Daha çox funksiya üçün planınızı yüksəldin."
                plans={['basic', 'pro']}
                currentPlan={user?.tenant_plan}
            />

            <div className="mb-6 flex flex-col gap-4 rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm md:flex-row md:items-end md:justify-between">
                <div>
                    <h1 className="text-3xl font-black text-gray-900">Dashboard</h1>
                    <p className="mt-1 text-sm text-gray-500">{dateStr}</p>
                    {showLicenseBanner && (
                        <div className={`mt-3 inline-flex rounded-2xl border px-4 py-2 text-sm font-semibold ${licenseBannerClass}`}>
                            Lizenziya bitmə vaxtı: {formatLicenseDate(licenseStatus.end_date)} ({licenseDaysLeft ?? '-'} gün)
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap gap-2 text-xs font-semibold">
                    <span className="rounded-full bg-primary-50 px-3 py-2 text-primary-700">Menyu</span>
                    <span className="rounded-full bg-slate-100 px-3 py-2 text-slate-700">Masalar</span>
                    <span className="rounded-full bg-amber-50 px-3 py-2 text-amber-700">{plan?.label || 'Pro'} paket</span>
                </div>
            </div>

            <div className="mb-6 grid grid-cols-2 gap-3 xl:grid-cols-5">
                {[
                    {
                        icon: '💰',
                        label: 'Bugünkü satış',
                        value: parseFloat(data?.today?.revenue || 0).toFixed(2),
                        note: `AZN${Number(data?.today?.service_amount || 0) > 0 ? ` (servis daxil)` : ''}`,
                        color: 'text-green-600'
                    },
                    {
                        icon: '🧮',
                        label: 'Servis faizi',
                        value: parseFloat(data?.today?.service_amount || 0).toFixed(2),
                        note: 'AZN bugün',
                        color: 'text-amber-600'
                    },
                    { icon: '🧾', label: 'Sifarişlər', value: data?.today?.order_count || 0, note: 'bugün', color: 'text-primary-600' },
                    { icon: '🍽️', label: 'Açıq masalar', value: `${data?.tables?.occupied || 0} / ${data?.tables?.total || 0}`, note: 'dolu / cəmi', color: 'text-amber-600' },
                    { icon: '👥', label: 'Ofisiantlar', value: data?.waiters || 0, note: 'aktiv', color: 'text-primary-600' }
                ].map((card) => (
                    <div key={card.label} className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
                        <div className="mb-3 text-2xl">{card.icon}</div>
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">{card.label}</div>
                        <div className={`mt-2 font-mono text-2xl font-black ${card.color}`}>{card.value}</div>
                        <div className="mt-1 text-xs text-gray-400">{card.note}</div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white xl:col-span-2">
                    <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                        <h3 className="font-bold text-gray-700">🧾 Son sifarişlər</h3>
                        <Link to="/admin/reports" className="text-xs font-medium text-primary-500 hover:underline">
                            Hamısına bax →
                        </Link>
                    </div>
                    {!data?.recent_orders?.length ? (
                        <div className="p-8 text-center text-gray-400">Hələ sifariş yoxdur.</div>
                    ) : freePlan ? (
                        <LockedPanel onUnlock={() => setShowUpgradeModal(true)}>{recentOrdersTable}</LockedPanel>
                    ) : recentOrdersTable}
                </div>

                <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
                    <div className="border-b border-gray-100 px-5 py-4">
                        <h3 className="font-bold text-gray-700">🏆 Bu ay ən çox satılanlar</h3>
                    </div>
                    {!data?.top_items?.length ? (
                        <div className="p-8 text-center text-sm text-gray-400">Hələ satış məlumatı yoxdur.</div>
                    ) : freePlan ? (
                        <LockedPanel onUnlock={() => setShowUpgradeModal(true)}>{topItemsList}</LockedPanel>
                    ) : topItemsList}
                </div>
            </div>
        </Layout>
    );
};

export default Dashboard;
